<script setup lang="ts">
import type { CourseFeeItem, FeeCountry } from '~/data/courseFees'
import type { Course } from '~/data/courses'
import type { Enrolment } from '~/data/enrolments'

const runtimeConfig = useRuntimeConfig()

// PROPS
const props = defineProps({
  course: { type: Object as PropType<Course>, required: true },
  enrolment: { type: Object as PropType<Enrolment>, required: true },
  showAccess: Boolean
})

const { data } = await useFetch('/api/fees', {
  method: 'GET',
  query: {
    ids: props.enrolment.fees
  }
})

const fees = data.value

if (!fees) {
  throw createError({
    statusCode: 404,
    message: 'Course fees not found',
    fatal: true
  })
}

// TODO Here we are assuming that all fees have the same countries
// This is not a good assumption, but lets fix it later
const countries = getCountriesFromPrices(fees[0].prices)

// REF
const open = ref(false)
const chosen = ref(0)

// Static image
const getCountryImg = (country: FeeCountry) => {
  return `/images/database/courses/flags/flag-${country.toLocaleLowerCase().replace(' ', '')}.png`
}

// View method
const chooseCountry = (index: number) => {
  chosen.value = index
  open.value = false
}

function toReadableAmount(amount: number) {
  // if the amount is larger than 1B, divide it by 1B and add 'B' at the end
  if (amount >= 1000000000) {
    return `${(amount / 1000000000).toFixed(1)}B`.replace('.0', '')
  }
  // if the amount is larger than 1M, divide it by 1M and add 'M' at the end
  else if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(1)}M`.replace('.0', '')
  }
  // if the amount is larger than 1000, divide it by 1000 and add 'k' at the end
  else if (amount >= 1000) {
    return `${(amount / 1000).toFixed(1)}k`.replace('.0', '')
  }

  return amount.toString()
}

const hasTwoFees = props.enrolment.fees && props.enrolment.fees?.length > 1

const getPrice = (prices: CourseFeeItem['prices']) =>
  prices.find((price) => price.country === countries[chosen.value])

function getApplyLink(fee: CourseFeeItem) {
  if (props.enrolment.direct) {
    // pay to enroll
    return `${runtimeConfig.public.lmsDomain}/pay-to-enroll?courseName=${props.course?.title}&courseId=${props.course?.id}&enrollmentId=${props.enrolment.id}&feeId=${fee.id}`
  } else {
    // apply to enroll
    return `${runtimeConfig.public.lmsDomain}/apply-to-enroll?courseName=${props.course?.title}&courseId=${props.course?.id}`
  }
}
</script>

<template>
  <div id="course-fee">
    <div v-if="enrolment.description && showAccess" id="course-fee-access">
      <p>{{ enrolment.description.title }}</p>
      <PageCoursesCourseCheckpoint
        v-for="point in enrolment.description.points"
        :key="point"
        :title="point"
      />
    </div>

    <h4 class="course-fee-label">Choose your country:</h4>
    <span v-if="enrolment.limitedRegions" id="course-fee-disclaimer"
      >* This course is only available in the regions below:</span
    >

    <div id="course-fee-dropdown" class="clickable">
      <div id="course-fee-dropdown-header" @click="open = !open">
        <img :src="getCountryImg(countries[chosen])" :alt="countries[chosen]" >
        <span>{{ countries[chosen] }}</span>
        <CommonIconChevron />
      </div>

      <div v-if="open" id="course-fee-dropdown-options">
        <div
          v-for="(country, index) in countries"
          :key="country"
          class="course-fee-option"
          @click="chooseCountry(index)"
        >
          <img :src="getCountryImg(country)" :alt="country" >
          {{ country }}
        </div>
      </div>
    </div>

    <h4 v-if="hasTwoFees" class="course-fee-label">Choose your option:</h4>

    <div id="course-fee-list">
      <div
        v-for="fee in fees"
        :key="fee.id"
        :class="`course-fee-item ${fees.length > 1 ? 'many-fees' : 'one-fee'}`"
      >
        <div class="course-fee-item-bg"/>

        <h2 v-if="hasTwoFees">{{ fee.title }}</h2>

        <div v-if="fee.prices" class="course-fee-item-price">
          <span>
            {{ getPrice(fee.prices)?.currency }}
          </span>

          <h1>{{ toReadableAmount(getPrice(fee.prices)?.discounted || 0) }}</h1>

          <h3 v-if="!hasTwoFees && getPrice(fee.prices)?.original">
            {{ toReadableAmount(getPrice(fee.prices)?.original || 0) }}
          </h3>

          <h4 v-else>{{ fee.frequency }}</h4>
        </div>

        <p v-if="fee.description" class="course-fee-item-description">
          {{ fee.description }}
        </p>

        <CommonButton arrow :href="getApplyLink(fee)">
          {{ enrolment.direct ? 'Purchase now' : 'Apply now' }}
        </CommonButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
#course-fee {
  .course-fee-label {
    font-size: 0.9rem;
    color: var(--textLight);
    padding-bottom: 5px;
  }

  &-disclaimer {
    font-size: 0.9rem;
    display: block;
    margin: -5px 0 10px;
    color: var(--textLight);
    font-style: italic;
  }

  &-access {
    margin-bottom: 40px;

    p {
      margin-bottom: 15px;
    }

    :deep(.check-point) {
      margin-top: 5px;
    }
  }

  &-dropdown {
    $height: 55px;
    $imgSize: 25px;

    height: $height;
    border: 1px solid gainsboro;
    width: 270px;
    border-radius: 10px;
    position: relative;
    margin-bottom: 40px;

    &-header {
      height: 100%;
      width: 100%;
      display: flex;
      align-items: center;
      padding: 0 20px;
      font-size: 1rem;

      img {
        height: $imgSize;
        width: $imgSize;
        border-radius: 50%;
        margin-right: 15px;
      }

      svg {
        $size: 13px;
        height: $size;
        width: $size;
        transform: rotate(90deg);
      }

      span {
        flex: 1;
      }
    }

    &-options {
      background-color: white;
      max-height: 300px;
      overflow-y: scroll;
      z-index: 50;
      position: absolute;
      top: $height;
      left: 0;
      width: 100%;
      border-radius: 10px;
      border: 1px solid gainsboro;
      box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);

      .course-fee-option {
        padding: 15px 20px;
        display: flex;
        align-items: center;
        font-size: 1rem;
        border-bottom: 1px solid gainsboro;

        img {
          height: $imgSize;
          width: $imgSize;
          margin-right: 15px;
        }
      }
    }
  }

  &-list {
    display: flex;
    gap: 20px;
    margin-top: 10px;
  }

  &-description {
    font-size: 1.2rem;
    width: 80%;
    margin-bottom: 30px;
  }

  &-header:hover,
  .course-fee-option:hover {
    background-color: var(--lightPurple);
  }
}

.course-fee-item {
  background-color: rgb(224, 238, 248);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  justify-content: space-between;
  border-radius: 15px;
  align-items: center;
  padding: 40px 50px;
  position: relative;
  flex: 1;

  $lightBlue: rgb(239, 248, 255);

  &-bg {
    height: 70%;
    width: 100%;
    position: absolute;
    bottom: 0;
    right: 0;
    border-bottom-right-radius: 100%;
    background-color: $lightBlue;
  }

  &-bg::before {
    position: absolute;
    content: '';
    top: -100%;
    left: 0;
    height: 100%;
    width: 100%;
    background-color: $lightBlue;
  }

  h2 {
    font-size: 1.3rem;
    margin-bottom: 20px;
    position: relative;
  }

  &-price {
    display: flex;
    align-items: flex-end;
    margin: 15px 0;
    position: relative;

    span {
      font-size: 1.3rem;
      padding-bottom: 4px;
      padding-right: 10px;
      color: var(--purple);
      color: black;
      font-weight: 500;
    }

    h1 {
      font-size: 3.5rem;
      line-height: 3.5rem;
      color: var(--purple);
      color: black;
    }

    h4,
    h3 {
      color: var(--textLight);
      padding-left: 20px;
      font-weight: 400;
    }

    h3 {
      padding-bottom: 6px;
      font-size: 2rem;
      text-decoration: line-through;
    }

    h4 {
      font-size: 1.2rem;
    }
  }

  &-description {
    flex: 1;
    position: relative;
    margin-bottom: 20px;
    font-size: 0.95rem;
    line-height: 1.45rem;
    color: var(--textMedium);
  }

  &-points {
    position: relative;
    $margin: 30px;
    margin-top: $margin;
    padding-top: $margin;
    border-top: 1px solid gainsboro;
    display: flex;
    flex-direction: column;
    gap: 10px;
    font-size: 1rem;
    line-height: 1.5rem;
  }

  &-point {
    padding-left: 25px;
    position: relative;
  }

  &-point::before {
    $size: 6px;
    background-color: var(--black);
    content: '';
    position: absolute;
    top: 8px;
    left: 0;
    height: $size;
    width: $size;
    border-radius: 50%;
  }

  :deep(.btn-arrow) {
    font-size: 0.97rem;
    margin-top: 20px;
    position: relative;
  }
}

.many-fees {
  padding: 40px;
  flex-direction: column;
  align-items: flex-start;

  h2 {
    font-size: 1.1rem;
  }

  .course-fee-item {
    &-price {
      margin: 30px 0 25px;

      h1 {
        font-size: 3rem;
        line-height: 3rem;
      }

      h4 {
        font-size: 1.1rem;
      }
    }
  }
}

.many-fees:nth-of-type(2n) {
  background-color: rgb(0, 45, 81);
  color: rgba(255, 255, 255, 0.7);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.3);

  .course-fee-item {
    &-bg::before,
    &-bg {
      background-color: rgb(0, 53, 96);
    }

    &-price {
      h1,
      span {
        color: rgba(255, 255, 255, 0.9);
      }

      h4,
      h3 {
        color: rgba(255, 255, 255, 0.4);
      }
    }

    &-description {
      color: rgba(255, 255, 255, 0.5);
    }

    &-points {
      border-top: 1px solid rgba(255, 255, 255, 0.2);
    }

    &-point::before {
      background-color: rgba(255, 255, 255, 0.7);
    }
  }
}

@media (max-width: 1200px) {
  .course-fee-item {
    :deep(.btn-arrow) {
      font-size: 0.95rem;
      padding: 10px 20px 10px 25px;
    }
  }

  .course-fee-item {
    padding: 35px 30px;
  }

  .many-fees {
    padding: 30px;
  }

  .course-fee-item,
  .many-fees {
    h2 {
      font-size: 1rem;
      margin-bottom: 25px;
    }

    .course-fee-item {
      &-price {
        margin: 0;

        span {
          padding-bottom: 1px;
        }

        h1 {
          font-size: 2.5rem;
          line-height: 2.5rem;
        }

        h4 {
          font-size: 1.1rem;
        }

        h3 {
          font-size: 1.5rem;
          padding-bottom: 4px;
        }
      }
    }

    :deep(.btn-arrow) {
      margin-top: 30px;
    }
  }
}

@media (max-width: 1100px) {
  #course-fee {
    &-list {
      display: grid;
    }
  }

  .course-fee-item,
  .many-fees {
    flex-direction: row;
    flex-wrap: wrap;
    position: relative;
    padding-right: 230px;

    h2 {
      width: 100%;
      margin: 0 0 20px;
      font-size: 0.95rem;
    }

    .course-fee-item {
      &-price {
        margin: 0;
        width: 100%;
      }

      &-description {
        width: 100%;
        margin-top: 25px;
      }
    }

    :deep(.btn-arrow) {
      position: absolute;
      top: 0;
      right: 30px;
      bottom: 0;
      margin: auto;
      height: 50px;
    }
  }
}

@media (max-width: 900px) {
  .course-fee-item {
    padding: 35px 25px;
  }

  .many-fees {
    padding: 25px;
  }

  .many-fees,
  .course-fee-item {
    padding-right: 200px;

    :deep(.btn-arrow) {
      right: 25px;
    }
  }
}

@media (max-width: 600px) {
  .course-fee-item {
    padding: 30px 25px;
  }

  .many-fees {
    padding: 25px;
  }

  .many-fees,
  .course-fee-item {
    display: block;

    h2 {
      font-size: 0.95rem;
    }

    .course-fee-item {
      &-price {
        span {
          font-size: 1rem;
          padding-bottom: 0;
        }

        h1 {
          font-size: 2.2rem;
          line-height: 2.2rem;
        }

        h3 {
          font-size: 1.2rem;
          padding-bottom: 2px;
          padding-left: 10px;
        }

        h4 {
          padding-left: 15px;
        }
      }

      &-description {
        margin-top: 30px;
        margin-bottom: 5px;
      }
    }

    :deep(.btn-arrow) {
      top: 0;
      right: 0;
      position: relative;
      margin-top: 25px;
      height: auto;
      padding: 9px 20px 9px 24px;
    }
  }
}
</style>
